<template>
  <!-- ====== 完整结果展示页（接真实数据版）====== -->
  <div class="result-page">
    <!-- ====== 第一行：固定顶栏（运行结果 | 耗时） ====== -->
    <div class="rp-top-bar">
      <span class="rp-top-bar-title">运行结果</span>
      <span class="rp-top-bar-time" v-if="elapsedMs > 0">耗时 {{ Math.round(elapsedMs / 1000) }} 秒</span>
    </div>

    <!-- ====== 输出内容主体（统一滚动条） ====== -->
    <div class="rp-output-scroll">
    <!-- ====== 报告标题栏（滚动内部，与统计卡同级） ====== -->
    <div class="rp-report-head" v-if="title || dataSources.length || hasActions">
      <div class="rp-report-info">
        <div class="rp-report-title">{{ title || '运行结果' }}</div>
        <div class="rp-report-subtitle">
          <span v-if="dataSources.length">
            <span v-for="(s, i) in dataSources" :key="s">
              {{ s }}<span v-if="i < dataSources.length - 1"> / </span>
            </span>
          </span>
          <span v-if="dataSources.length" class="rp-dot">·</span>
          <span class="rp-sub-export">{{ exports.join(' / ') }} 导出</span>
        </div>
      </div>
      <div class="rp-report-actions">
        <button class="rp-action-btn" v-if="copyText || markdown" @click="copyReport">复制报告</button>
        <button class="rp-action-btn" v-if="docxBase64" @click="downloadDocx">下载 Word</button>
        <button class="rp-action-btn" v-if="copyText || markdown" @click="downloadTxt">下载 TXT</button>
      </div>
    </div>

    <!-- ====== 4 个统计卡 ====== -->
    <div class="rp-stats-row" v-if="hasStats">
      <div class="rp-stat-card">
        <div class="rp-stat-label">筛选结果</div>
        <div class="rp-stat-value">{{ stats.totalCount }}</div>
        <div class="rp-stat-note">智能关键词扩展</div>
      </div>
      <div class="rp-stat-card">
        <div class="rp-stat-label">返回文献</div>
        <div class="rp-stat-value">{{ stats.pageCount }}</div>
        <div class="rp-stat-note">本页 {{ stats.pageCount }} 篇</div>
      </div>
      <div class="rp-stat-card">
        <div class="rp-stat-label">近 {{ stats.yearsSpan }} 年</div>
        <div class="rp-stat-value">{{ stats.recentCount }}</div>
        <div class="rp-stat-note">本次结果</div>
      </div>
      <div class="rp-stat-card">
        <div class="rp-stat-label">高引用 / Q1</div>
        <div class="rp-stat-value">{{ stats.q1Count }}</div>
        <div class="rp-stat-note">高引 {{ stats.highCitationCount }} · 高 IF {{ stats.highIfCount }}</div>
      </div>
    </div>

    <!-- ====== 底部数据源 chip 行 ====== -->
    <div class="rp-source-bar" v-if="dataSourceChips.length">
      <span
        v-for="chip in dataSourceChips"
        :key="chip.text"
        :class="['rp-source-chip', `rp-source-chip--${chip.type}`]"
      >{{ chip.text }}</span>
    </div>

    <!-- ====== 筛选条件 + 检索关键词折叠面板（综合来源下方） ====== -->
    <!-- 筛选条件（默认折叠） -->
    <div class="rp-keyword-bar" v-if="filters.length">
      <details class="rp-keyword-details">
        <summary class="rp-keyword-summary">
          <span class="rp-keyword-icon">▶</span>
          <span class="rp-keyword-title">筛选条件</span>
          <span class="rp-keyword-count">{{ filters.length }} 项</span>
        </summary>
        <div class="rp-keyword-body rp-filter-body">
          <div v-for="(item, i) in filters" :key="i" class="rp-filter-item">{{ item }}</div>
        </div>
      </details>
    </div>
    <!-- 检索关键词（默认折叠） -->
    <div class="rp-keyword-bar" v-if="keywords.length">
      <details class="rp-keyword-details">
        <summary class="rp-keyword-summary">
          <span class="rp-keyword-icon">▶</span>
          <span class="rp-keyword-title">检索关键词</span>
          <span class="rp-keyword-count">{{ keywords.length }} 个</span>
        </summary>
        <div class="rp-keyword-body">
          <span v-for="kw in keywords" :key="kw" class="rp-keyword-chip" :title="kw">{{ kw }}</span>
        </div>
      </details>
    </div>

    <!-- ====== 文献结果表（原生 <table>·table-layout: fixed 列宽可控，修复 5 列 grid 标题列错乱） ====== -->
    <div class="rp-literature-list" v-if="literatureList.length">
      <!-- 表格标题（项目 + 总数） -->
      <div class="rp-literature-head">
        <span class="rp-lit-head-label">文献结果表</span>
        <span class="rp-lit-head-count">共 {{ literatureList.length }} 篇</span>
      </div>
      <table class="rp-lit-table">
        <colgroup>
          <col class="rp-col-num" />
          <col class="rp-col-title" />
          <col class="rp-col-journal" />
          <col class="rp-col-metrics" />
          <col class="rp-col-links" />
        </colgroup>
        <thead>
          <tr>
            <th class="rp-th-num">#</th>
            <th class="rp-th-title">题名</th>
            <th class="rp-th-journal">来源/期刊</th>
            <th class="rp-th-metrics">指标</th>
            <th class="rp-th-links">链接/DOI</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(lit, i) in literatureList" :key="i" class="rp-literature-card">
            <td class="rp-td-num">{{ lit.idx }}</td>
            <td class="rp-td-title">
              <div class="rp-lit-title">
                <a v-if="lit.url" :href="lit.url" target="_blank" rel="noopener" class="rp-lit-title-link" :title="lit.title">{{ lit.title || '—' }}</a>
                <span v-else :title="lit.title">{{ lit.title || '—' }}</span>
              </div>
              <div class="rp-lit-meta">
                <span v-if="lit.year" class="rp-lit-year">{{ lit.year }}</span>
                <span v-if="lit.year && (lit.journal || lit.source)" class="rp-lit-sep">·</span>
                <span v-if="lit.journal" class="rp-lit-journal">{{ lit.journal }}</span>
                <span v-if="lit.journal && lit.source" class="rp-lit-sep">·</span>
                <span v-if="lit.source" class="rp-lit-source">{{ lit.source }}</span>
              </div>
            </td>
            <td class="rp-td-journal">
              <div class="rp-lit-journal" :title="lit.journal">{{ lit.journal || '—' }}</div>
              <div v-if="lit.source" class="rp-lit-source-tag">{{ lit.source }}</div>
            </td>
            <td class="rp-td-metrics">
              <div class="rp-lit-metrics">
                <span v-if="lit.if" class="rp-lit-chip rp-lit-chip-if">IF {{ lit.if }}</span>
                <span v-if="lit.quartile" class="rp-lit-chip rp-lit-chip-q">{{ lit.quartile }}</span>
                <span v-if="lit.cas" class="rp-lit-chip rp-lit-chip-cas">中科院 {{ lit.cas }} 区</span>
                <span v-if="lit.citations" class="rp-lit-chip rp-lit-chip-cite">引用 {{ lit.citations }}</span>
              </div>
            </td>
            <td class="rp-td-links">
              <div class="rp-lit-links">
                <a v-if="lit.openalexId" :href="lit.url" target="_blank" rel="noopener" class="rp-lit-link">OpenAlex {{ lit.openalexId }}</a>
                <a v-else-if="lit.ssId" :href="'https://www.semanticscholar.org/paper/' + lit.ssId" target="_blank" rel="noopener" class="rp-lit-link">Semantic Scholar</a>
                <a v-else-if="lit.url" :href="lit.url" target="_blank" rel="noopener" class="rp-lit-link">{{ lit.label || '链接' }}</a>
                <a v-if="lit.doi" :href="'https://doi.org/' + lit.doi" target="_blank" rel="noopener" class="rp-lit-link rp-lit-doi">DOI: {{ lit.doi }}</a>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- ====== 综合分析卡片（包含「## 文献结果概览」段之后到结尾的所有内容：## 研究现状与前沿 + ## 研究热点 + ... + ## 文献脉络图表） ====== -->
    <div class="rp-analysis-card" v-if="comprehensiveAnalysis">
      <div class="rp-analysis-head">
        <div class="rp-analysis-title">综合分析</div>
        <div v-if="analysisChipCount" class="rp-analysis-chip">基于 {{ analysisChipCount }} 次返回文献</div>
      </div>
      <div class="rp-analysis-body">
        <div class="rp-analysis-content" v-html="renderMarkdown(comprehensiveAnalysis)"></div>
      </div>
    </div>

    <!-- /rp-output-scroll end -->
    </div>

    <!-- 真实内容区：渲染后端返回的 markdown（已移除「## 文献结果表」段 +「## 综合分析」段） -->
    <div class="rp-content" v-if="contentMarkdown" v-html="renderMarkdown(contentMarkdown)"></div>

    <!-- 无 markdown 时不展示（避免占位）-->
    <template v-else-if="!markdown"><!-- 等待真实 markdown 数据 --></template>
  </div>
</template>

<script>
export default {
  name: 'ResultPage',
  props: {
    // 后端 resultObj 透传字段
    title: { type: String, default: '' },
    toolId: { type: String, default: '' },
    elapsedMs: { type: Number, default: 0 },
    markdown: { type: String, default: '' },
    copyText: { type: String, default: '' },
    docxBase64: { type: String, default: '' },
    docxFilename: { type: String, default: '' },
    // 后端结构化摘要（蛇形命名，优先于正则提取）
    // 字段：total_count / fetched_count / returned_count / recent_count / q1_count / high_citation_count / high_if_count
    summary: { type: Object, default: () => ({}) }
  },
  computed: {
    // 是否有操作按钮（控制第二行报告头是否显示）
    hasActions() {
      return !!(this.copyText || this.markdown || this.docxBase64)
    },
    // 从 markdown 中提取数据源名称
    dataSources() {
      const md = this.markdown || ''
      const known = ['Semantic Scholar', 'OpenAlex', 'Crossref', 'arXiv', 'PubMed', 'Google Scholar', '知网', '万方']
      return known.filter(s => md.includes(s))
    },
    // 导出格式
    exports() {
      const exps = []
      if (this.docxBase64) exps.push('Word')
      if (this.markdown || this.copyText) exps.push('TXT')
      return exps
    },
    // 4 个统计数字（【优先】从后端结构化 summary 读取，markdown 正则作 fallback）
    stats() {
      const md = this.markdown || ''
      const s = this.summary || {}
      const result = {
        totalCount: 0, pageCount: 0, recentCount: 0, q1Count: 0,
        highCitationCount: 0, highIfCount: 0, yearsSpan: 5
      }
      // —— 优先：后端结构化 summary（蛇形命名 + 兼容驼峰）——
      if (s.total_count || s.totalCount) {
        result.totalCount = s.total_count || s.totalCount
      }
      if (s.fetched_count || s.fetchedCount) {
        if (!result.totalCount) result.totalCount = s.fetched_count || s.fetchedCount
      }
      if (s.returned_count || s.returnedCount) {
        result.pageCount = s.returned_count || s.returnedCount
      }
      if (s.recent_count || s.recentCount) {
        result.recentCount = s.recent_count || s.recentCount
      }
      if (s.q1_count || s.q1Count) {
        result.q1Count = s.q1_count || s.q1Count
      }
      if (s.high_citation_count !== undefined) result.highCitationCount = s.high_citation_count
      if (s.high_if_count !== undefined) result.highIfCount = s.high_if_count
      result.yearsSpan = s.years_span || s.yearsSpan || 5
      // —— Fallback：markdown 摘要区正则（仅前 2 个字段）——
      if (!result.totalCount) {
        const totalMatch = md.match(/(?:候选结果总量|共检索到|共检索|检索结果|共|找到|检索到)[:：]?\s*(\d+)\s*篇?/)
        if (totalMatch) result.totalCount = parseInt(totalMatch[1])
      }
      if (!result.pageCount) {
        const pageMatch = md.match(/(?:本次纳入结果|本次纳入|纳入结果|返回文献|返回)\s*(\d+)\s*篇?/)
        if (pageMatch) result.pageCount = parseInt(pageMatch[1])
      }
      // 近 N 年 / Q1：摘要区没有这 2 个字段，禁用会误中的旧正则
      // （旧"近 N 年 M 篇"会误中"近年来"；旧"Q1...数字"会误中"IF 9.5 / Q1 / 中科院 1 区"）
      return result
    },
    hasStats() {
      const s = this.stats
      return s.totalCount > 0 || s.pageCount > 0 || s.recentCount > 0 || s.q1Count > 0 || s.highCitationCount > 0
    },
    // 底部 chips：分类型（meta=中性标签，source=数据源）
    dataSourceChips() {
      const chips = [{ type: 'meta', text: '综合来源' }]
      const md = this.markdown || ''
      // 年份范围（meta 中性）
      const yearMatch = md.match(/(\d{4})\s*[-–]\s*(\d{4})/)
      if (yearMatch) chips.push({ type: 'meta', text: `年份 ${yearMatch[1]}-${yearMatch[2]}` })
      // 数据源名（每个独立 source 绿色 chip）
      const sources = this.dataSources
      sources.forEach(s => chips.push({ type: 'source', text: s }))
      return chips
    },
    // 【新增】从 markdown 中提取「检索关键词」列表（已独立显示为折叠面板）
    //   关键：后端返回的是「## 检索关键词」二级标题 + 反引号行内代码包裹的关键词
    keywords() {
      const md = this.markdown || ''
      // 匹配整段「# 检索关键词」+ 1+ 行非空内容
      //   分两步：①到空行/标题 ②到字符串末尾（避免 $ 匹配行尾导致提前结束）
      const m1 = md.match(/^#{1,6}[ \t]*检索关键词[ \t]*$\n([\s\S]+?)(?=\n[ \t]*\n|\n#{1,6}[ \t]+)/m)
      const m2 = !m1 && md.match(/^#{1,6}[ \t]*检索关键词[ \t]*$\n([\s\S]+?)$/m)
      const match = m1 || m2
      if (!match) return []
      // 去掉反引号（行内代码）包裹
      const block = match[1].replace(/`/g, '')
      // 按空白字符（空格 / Tab）分隔关键词，去重 + 过滤空
      return [...new Set(block.split(/\s+/).filter(Boolean))]
    },
    // 【新增】从 markdown 中提取「筛选条件」列表
    //   关键：后端返回的是裸「筛选条件」标题（无 #）+ - 或 * 列表项
    //   3 行：年份范围 / 检索说明 / 分析说明
    filters() {
      const md = this.markdown || ''
      // 匹配整段「筛选条件」+ 1+ 行列表项（- 或 * 开头）
      //   分两步：①到空行/标题 ②到字符串末尾
      const m1 = md.match(/^[ \t]*筛选条件[ \t]*$\n((?:[-*]\s+[^\n]*\n?)+?)(?=\n[ \t]*\n|\n#{1,6}[ \t]+)/m)
      const m2 = !m1 && md.match(/^[ \t]*筛选条件[ \t]*$\n((?:[-*]\s+[^\n]*\n?)+)$/m)
      const match = m1 || m2
      if (!match) return []
      // 解析每行（去掉 - 或 * 前缀 + trim）
      const lines = match[1].split('\n').map(l => l.trim()).filter(l => l)
      return lines.map(l => l.replace(/^[-*]\s+/, '').trim()).filter(Boolean)
    },
    // 【新增】从 markdown 中提取「## 文献结果表」段下的 GFM 表格，解析为对象数组
    //   用于渲染为卡片列表（替代默认 GFM 表格样式）
    //   健壮策略：列名模糊匹配 + 列位置兜底
    literatureList() {
      const md = this.markdown || ''
      // 1. 找到「## 文献结果表」段（GFM 表格专在段中；原 0.1.0 老板反馈表格在「## 文献结果概览」段是误读，实测在「## 文献结果表」段）
      //   终止条件 = 下一个 ## 标题（不含 ###）或字符串末尾——不能以空行作终止，否则会丢掉表格
      const m = md.match(/^#{1,6}[ \t]*文献结果表[ \t]*$\n([\s\S]+?)(?=\n##?[ \t]+|(?![\s\S]))/m)
      if (!m) return []
      const block = m[1]
      // 2. 在段内找 GFM 表格（表头 + 分隔符 + 数据行）
      const tm = block.match(/((?:^\|.+\|\s*\n)+)(^\|[\s\-:|]+\|\s*\n)((?:^\|.+\|\s*\n?)+)/m)
      if (!tm) return []
      const parseRow = (line) => line.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map(c => c.trim())
      const headerLine = tm[1].split('\n').find(l => l.trim().startsWith('|'))
      if (!headerLine) return []
      const headers = parseRow(headerLine)
      const dataRows = tm[3].trim().split('\n').filter(l => l.trim().startsWith('|')).map(parseRow)
      // 3. 列名归一化：去掉内部空格/标点，匹配常用别名
      const norm = (s) => (s || '').replace(/[\s\/　]/g, '').toLowerCase()
      const findCol = (...candidates) => {
        for (const c of candidates) {
          const idx = headers.findIndex(h => norm(h) === norm(c))
          if (idx >= 0) return idx
        }
        // 模糊匹配（包含）
        for (const c of candidates) {
          const idx = headers.findIndex(h => norm(h).includes(norm(c)))
          if (idx >= 0) return idx
        }
        return -1
      }
      // 4. 各字段列索引（按列名匹配 → 找不到再按位置）
      const idxIdx = findCol('#', '序号', '编号', 'no', 'num') // # 序号
      const yearIdx = findCol('年份', 'year', 'date')         // 年份
      const titleIdx = findCol('题目', '题名', '标题', 'title') // 题目
      const sourceIdx = findCol('来源', 'source', '数据源')    // 来源
      const journalIdx = findCol('期刊/会议', '期刊', '会议', 'journal', 'venue') // 期刊/会议
      const citeIdx = findCol('引用', 'citations', 'cited')    // 引用
      const metricsIdx = findCol('质量指标', '指标', 'metrics', 'impact') // 质量指标
      const linkIdx = findCol('链接/DOI', '链接', 'doi', 'link', 'url') // 链接/DOI
      // 5. 解析质量指标列：拆分 IF / Q1 / 中科院 X 区
      const parseMetrics = (raw) => {
        const parts = (raw || '').split(/[\/／]/).map(s => s.trim()).filter(Boolean)
        const obj = { if: '', quartile: '', cas: '' }
        parts.forEach(p => {
          if (/^IF\s*/i.test(p)) obj.if = p.replace(/^IF\s*/i, '').trim()
          else if (/^Q[1-4]$/i.test(p)) obj.quartile = p.toUpperCase()
          else if (/中科院/.test(p)) obj.cas = p.replace(/中科院\s*/, '').replace(/\s*区$/, '').trim()
        })
        return obj
      }
      // 6. 解析链接/DOI 列：识别 DOI / OpenAlex / Semantic Scholar
      //    真实后端返回的格式是 markdown 链接：[text](url)
      //    例：[2c4cfcbe...](https://www.semanticscholar.org/paper/2c4cfcbe...) 或 [W4415373306](https://api.openalex.org/works/W4415373306) DOI: 10.xxxx
      const parseLink = (raw) => {
        const s = (raw || '').trim()
        if (!s) return { url: '', label: '', doi: '', openalexId: '', ssId: '' }
        // 6a. markdown 链接格式 [text](url)
        const mdLink = s.match(/^\[([^\]]+)\]\(([^)]+)\)/)
        if (mdLink) {
          const text = mdLink[1]
          const url = mdLink[2]
          const doiM = url.match(/(10\.\d{4,}\/[^\s<>"&]+)/i) || text.match(/(10\.\d{4,}\/[^\s<>"&]+)/i)
          const doi = doiM ? doiM[1] : ''
          const oaM = url.match(/W(\d+)/i) || text.match(/W(\d+)/i)
          const openalexId = oaM ? ('W' + oaM[1]) : ''
          const ssM = (text + url).match(/[a-f0-9]{32}/i)
          return {
            url,
            label: openalexId ? ('OpenAlex ' + openalexId) : (ssM ? 'Semantic Scholar' : text),
            doi, openalexId,
            ssId: ssM ? ssM[0] : ''
          }
        }
        // 6b. 裸文本
        const doiM = s.match(/(10\.\d{4,}\/[^\s<&"<>]+)/)
        const doi = doiM ? doiM[1] : ''
        const oaM = s.match(/W(\d+)/i) || s.match(/openalex[:：]?\s*(\d{4,})/i)
        const openalexId = oaM ? ('W' + (oaM[1] || oaM[0].match(/\d+/)[0])) : ''
        const ssM = s.match(/[a-f0-9]{32}/i)
        return {
          url: doi ? 'https://doi.org/' + doi : (openalexId ? 'https://api.openalex.org/works/' + openalexId : ''),
          label: openalexId ? ('OpenAlex ' + openalexId) : (ssM ? ('SS ' + ssM[0]) : ''),
          doi, openalexId,
          ssId: ssM ? ssM[0] : ''
        }
      }
      // 7. 组装结果
      return dataRows.map((row, ri) => {
        const get = (idx) => idx >= 0 ? (row[idx] || '') : ''
        const metrics = parseMetrics(get(metricsIdx))
        const link = parseLink(get(linkIdx))
        return {
          idx: get(idxIdx) || String(ri + 1),
          year: get(yearIdx),
          title: get(titleIdx),
          source: get(sourceIdx),
          journal: get(journalIdx),
          citations: get(citeIdx),
          ...metrics,
          ...link
        }
      })
    },
    // 【重写 2026-06-18 01:53】从 markdown 提取「## 综合分析」段及以下所有子段（## 研究现状与前沿 + ## 研究热点 + ... + ## 文献脉络图谱）
    //   根因：老板反馈 01:51——“现在把表格也加到了综合分析里面了”——上一版从「## 文献结果概览」段后到结尾提取，误把「## 文献结果表」段（含表格）当成了综合分析一部分
    //   正确：表格在「## 文献结果表」段（独立段），不在「## 综合分析」段内。综合分析从「## 综合分析」标题行开始，到「## 导出内容」段之前结束
    //   修正：提取范围 = 「## 综合分析」标题行 -> 「## 导出内容」标题行之前
    comprehensiveAnalysis() {
      const md = this.markdown || ''
      // 1. 找「## 综合分析」标题行的位置
      const startM = md.match(/^#{1,6}[ \t]*综合分析[ \t]*$/m)
      if (!startM) return ''
      // 2. 找「## 导出内容」标题行的位置（作为终点）
      const endM = md.match(/^#{1,6}[ \t]*导出内容[ \t]*$/m)
      const endIdx = endM ? endM.index : md.length
      return md.substring(startM.index, endIdx).trim()
    },
    // 【新增】综合分析卡片右上角 chip 数字
    //   策略：优先「返回文献」数（pageCount），无则用「筛选结果」数（totalCount）
    analysisChipCount() {
      return this.stats.pageCount || this.stats.totalCount
    },
    // 【重写 2026-06-18 01:53】从 markdown 完全清空 contentMarkdown
    //   原因：老板反馈 01:51——“现在把表格也加到了综合分析里面了，把原来的表格格式也都打乱了”
    //   根因：上一版保留「## 文献结果概览」段（meta 列表），与顶部 stats 卡片重复
    //   修复：contentMarkdown = ""，底部 v-if="contentMarkdown" 不渲染，无重复
    //   所有信息已划分到独立卡片：
    //     - 顶部 title 卡片 = 主标题
    //     - 4 stats 卡片 = meta 列表（候选/纳入/策略/排序等）
    //     - 折叠面板 = 「## 筛选条件」+「## 检索关键词」段
    //     - literatureList 卡片 = 「## 文献结果表」段（GFM 表格）
    //     - comprehensiveAnalysis 卡片 = 「## 综合分析」段 + 8 个子段
    contentMarkdown() {
      // 保留接口，底部 v-if="contentMarkdown" 不渲染
      return ''
    }
  },
  methods: {
    copyReport() {
      const text = this.copyText || this.markdown
      if (!text) return
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(() => {
          this._toast('已复制到剪贴板')
        }).catch(() => this._fallbackCopy(text))
      } else {
        this._fallbackCopy(text)
      }
    },
    _fallbackCopy(text) {
      const ta = document.createElement('textarea')
      ta.value = text
      ta.style.position = 'fixed'
      ta.style.opacity = '0'
      document.body.appendChild(ta)
      ta.select()
      try { document.execCommand('copy'); this._toast('已复制到剪贴板') } catch (e) { this._toast('复制失败') }
      document.body.removeChild(ta)
    },
    downloadDocx() {
      if (!this.docxBase64) return
      const filename = this.docxFilename || (this.title ? this.title + '.docx' : 'result.docx')
      try {
        let b64 = this.docxBase64
        const dataIdx = b64.indexOf('base64,')
        if (dataIdx >= 0) b64 = b64.substring(dataIdx + 7)
        const binary = atob(b64)
        const len = binary.length
        const bytes = new Uint8Array(len)
        for (let i = 0; i < len; i++) bytes[i] = binary.charCodeAt(i)
        const blob = new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = filename
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        setTimeout(() => URL.revokeObjectURL(url), 1000)
      } catch (e) {
        this._toast('下载失败：' + (e.message || e))
      }
    },
    downloadTxt() {
      const text = this.copyText || this.markdown
      if (!text) return
      const filename = (this.title ? this.title : 'result') + '.txt'
      const blob = new Blob([text], { type: 'text/plain;charset=utf-8' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      setTimeout(() => URL.revokeObjectURL(url), 1000)
    },
    _toast(msg) {
      // 简单提示（避免阻塞弹 alert）
      try {
        if (window.$toast) { window.$toast(msg); return }
      } catch (e) {}
      alert(msg)
    },
    renderMarkdown(md) {
      if (!md || typeof md !== 'string') return ''
      let html = md
      // -0. 删除综合分析内容区开头的重复标题（如「## 综合分析」或「### 综合分析」），避免跟外层卡片标题重复
      html = html.replace(/^(?:#{1,4}\s+综合分析\s*\n?)+/, '')
      // 0. 删除「筛选条件」+「检索关键词」整段（已提取到 filters / keywords computed 中独立显示为折叠面板）
      //    关键：后端返回的真实格式——「筛选条件」裸标题+`-`列表、「## 检索关键词」二级标题+`反引号`行内代码
      //    分两步（关键！避免 $ 匹配行尾导致提前结束）：①中间段（终点=空行/标题）②末尾段（终点=字符串末尾）
      // 0a. 删除「筛选条件」整段
      html = html.replace(/^[ \t]*筛选条件[ \t]*$\n((?:[-*]\s+[^\n]*\n?)+?)(?=\n[ \t]*\n|\n#{1,6}[ \t]+)/gm, '')
      html = html.replace(/^[ \t]*筛选条件[ \t]*$\n((?:[-*]\s+[^\n]*\n?)+)$/gm, '')
      // 0b. 删除「检索关键词」整段
      html = html.replace(/^#{1,6}[ \t]*检索关键词[ \t]*$\n(?:[^\n]+\n)+?(?=\n[ \t]*\n|\n#{1,6}[ \t]+)/gm, '')
      html = html.replace(/^#{1,6}[ \t]*检索关键词[ \t]*$\n(?:[^\n]+\n)+$/gm, '')
      // 0c. 【旧版双保险逻辑已删除】新设计中 contentMarkdown 已在 computed 层裁切到「## 文献结果概览」段结尾，无需重复删
      // 1. 转义 HTML（先做）
      html = html.replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
      // 2. 代码块
      html = html.replace(/```(\w*)\n([\s\S]*?)```/g, (m, lang, code) => '<pre class="md-code"><code>' + code + '</code></pre>')
      // 3. 标题
      html = html.replace(/^######\s+(.+)$/gm, '<h6>$1</h6>')
        .replace(/^#####\s+(.+)$/gm, '<h5>$1</h5>')
        .replace(/^####\s+(.+)$/gm, '<h4>$1</h4>')
        .replace(/^###\s+(.+)$/gm, '<h3>$1</h3>')
        .replace(/^##\s+(.+)$/gm, '<h2>$1</h2>')
        .replace(/^#\s+(.+)$/gm, '<h1>$1</h1>')
      // 4. GFM 表格：表头 + 分隔符 + 数据行（必须在段落处理之前）
      html = html.replace(/((?:^\|.+\|\s*\n)+)(^\|[\s\-:|]+\|\s*\n)((?:^\|.+\|\s*\n?)+)/gm, (m, headerBlock, sepBlock, dataBlock) => {
        const parseRow = (line) => {
          return line.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map(c => c.trim())
        }
        const firstHeaderLine = headerBlock.split('\n').find(l => l.trim().startsWith('|'))
        if (!firstHeaderLine) return m
        const headers = parseRow(firstHeaderLine)
        const dataRows = dataBlock.trim().split('\n').filter(line => line.trim().startsWith('|')).map(parseRow)
        let t = '<div class="rp-md-table-wrap"><table class="rp-md-table"><thead><tr>'
        headers.forEach(h => { t += '<th>' + h + '</th>' })
        t += '</tr></thead><tbody>'
        dataRows.forEach(row => {
          t += '<tr>'
          headers.forEach((_, i) => {
            t += '<td>' + (row[i] !== undefined ? row[i] : '') + '</td>'
          })
          t += '</tr>'
        })
        t += '</tbody></table></div>'
        return t + '\n'
      })
      // 5. 多行伪表格（2+ 连续行 |...|），按行内 | 拆 cell
      html = html.replace(/(?:^\|[^\n]+\|\s*\n?){2,}/gm, (block) => {
        const lines = block.trim().split('\n').filter(l => l.trim().startsWith('|') && l.trim().endsWith('|'))
        if (lines.length < 2) return block
        const out = lines.map(line => {
          const cells = line.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map(c => c.trim()).filter(c => c !== '')
          if (cells.length === 0) return ''
          if (cells.every(c => /^\d+\.\s/.test(c))) {
            return '<div class="rp-md-list rp-md-list-num">' + cells.map(c => '<div class="rp-md-li">' + c.replace(/^\d+\.\s+/, '') + '</div>').join('') + '</div>'
          }
          return cells.map(c => '<p class="rp-md-pipe-line">' + c + '</p>').join('')
        }).join('\n')
        return out + '\n'
      })
      // 6. 单行伪表格 | a | b | c | → 多 cell 拆 p 或 ol
      html = html.replace(/^\|([^\n]+)\|$/gm, (m, content) => {
        const cells = content.split('|').map(c => c.trim()).filter(c => c !== '')
        if (cells.length < 2) return m
        if (cells.every(c => /^\d+\.\s/.test(c))) {
          return '<div class="rp-md-list rp-md-list-num">' + cells.map(c => '<div class="rp-md-li">' + c.replace(/^\d+\.\s+/, '') + '</div>').join('') + '</div>'
        }
        return cells.map(c => '<p class="rp-md-pipe-line">' + c + '</p>').join('\n')
      })
      // 7. 引用块
      html = html.replace(/^>\s+(.+)$/gm, '<blockquote>$1</blockquote>')
      // 8. 粗体 / 斜体
      html = html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
      html = html.replace(/(^|[^*])\*([^*\n]+)\*/g, '$1<em>$2</em>')
      // 9. 行内代码
      html = html.replace(/`([^`\n]+)`/g, '<code class="md-inline-code">$1</code>')
      // 10. 引用信息徽章：(2024年, Nature, 引用270次) / (2024, Nature, 270)
      html = html.replace(/[（(]\s*(\d{4})\s*年?,?\s*[,，]?\s*([^,，()]+?)\s*[,，]\s*引用?\s*(\d+)\s*次?\s*[)）]/g,
        '<span class="rp-citation">$1 · $2 · 引用 $3</span>')
      // 11+12. DOI 链接（占位符机制，避免重复包裹）
      //     先把 DOI（含 DOI: 前缀或裸 DOI）全部替换为占位符，其他规则不会误伤
      const doiHolder = []
      html = html.replace(/(DOI[:：]\s*)?(10\.\d{4,}\/[^\s<&"<>]+)/gi, (m, prefix, doi) => {
        const idx = doiHolder.length
        doiHolder.push({ prefix: prefix || '', doi: doi })
        return '\x00DOI' + idx + '\x00'
      })
      // 占位符会在所有其他规则处理完后还原为 HTML（见 17.5 步）
      // 13. 链接 [text](url)
      html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener">$1</a>')
      // 14. 裸 URL
      html = html.replace(/(?<!["=>])(https?:\/\/[^\s<"]+)(?!<\/a>|"<)/g,
        '<a href="$1" target="_blank" rel="noopener">$1</a>')
      // 15. 水平线
      html = html.replace(/^---+$/gm, '<hr/>')
      // 15.5. 单行内多列表项折行（把 "item * item * item" 拆成多行）
      //   关键：列表项标记 * 的"前面是空白/中英文标点"+"后面是空格"是"列表项开始"的强特征
      //   与行内强调 *a* 的区别：
      //     强调标记前面是非空白字符（a）或不接空格（紧接 *）——不被识别
      //     列表项标记前面是空白/标点，且后面必须接"空格+内容"——被识别
      //   仅当一行内 >= 2 个列表项标记时才拆（避免无谓修改单项目行）
      //   适用范围：* **首选：xxx** * **备选：yyy**、item. * next、内容。* 备选
      //   注意：- 不作为列表项标记（太容易误伤英文连字符 "Q1) - xxx"）
      html = html.split('\n').map(line => {
        // 列表项标记 *：前面是空白/行首/常见中英文标点；后面必须是空格
        const re = /(?<=^|[\s\u4e00-\u9fff，。：；、！？（）]|[.,;:!?\(\)\[\]"'`])(\*)(?=\s)/g
        const matches = []
        let mm
        while ((mm = re.exec(line)) !== null) matches.push(mm.index)
        if (matches.length < 2) return line
        // 保留行首缩进（仅当行首是空白时）
        const indent = (line.match(/^[ \t]*/) || [''])[0]
        let result = line.substring(0, matches[0] + 2) // 行首到第一个标记+空格
        let lastEnd = matches[0] + 2
        for (let i = 1; i < matches.length; i++) {
          const idx = matches[i]
          // idx-1 是标记前的"空白/标点"，在 idx-1 处插入 \n + 缩进
          // 追加"上一个项目内容（lastEnd 到 idx-1）+ 换行 + 缩进 + 标记 + 空格"
          result += line.substring(lastEnd, idx - 1) + '\n' + indent + line.substring(idx, idx + 2)
          lastEnd = idx + 2
        }
        // 追加最后一个项目内容
        result += line.substring(lastEnd)
        return result
      }).join('\n')
      // 16. 无序列表（- 或 * 开头，按行首缩进递归识别嵌套层级）
      //   每多 2 空格缩进 = 多 1 级嵌套
      //   输出：递归嵌套 <div class="rp-md-list">，CSS 通过累加 padding-left 实现缩进
      //   原 bug：[(?:[ \t]*[-*]\s+.+\n?)+] 不识别缩进层级（"父"和"子"同层）
      //   修复：扫描所有连续项的缩进宽度，递归构建嵌套结构
      html = html.replace(/(^|\n)((?:[ \t]*[-*]\s+.+\n?)+)/g, (m, prefix, block) => {
        // 遇到数字列表行（\d+. 开头）时截断，避免跨组合并
        const rawLines = block.split('\n')
        const lines = []
        for (const l of rawLines) {
          if (/^\s*\d+\.\s/.test(l)) break
          if (l.trim()) lines.push(l)
        }
        if (lines.length === 0) return m
        const items = lines.map(l => {
          const im = l.match(/^([ \t]*)/)[1]
          const iw = im.replace(/\t/g, '    ').length
          const c = l.replace(/^[ \t]*[-*]\s+/, '').trim()
          return { indent: iw, content: c }
        })
        const minIndent = Math.min(...items.map(i => i.indent))
        const render = (arr, base) => {
          const out = []
          let i = 0
          while (i < arr.length) {
            const it = arr[i]
            if (it.indent < base) break
            if (it.indent === base) {
              const sub = []
              let j = i + 1
              while (j < arr.length && arr[j].indent > base) {
                sub.push(arr[j])
                j++
              }
              if (sub.length > 0) {
                const subMin = Math.min(...sub.map(s => s.indent))
                // 子 ul 前后加换行，避免 16.5 步平衡 div 扫描时跨块
                out.push('<div class="rp-md-li">' + it.content + '\n' + render(sub, subMin) + '\n</div>')
                i = j
              } else {
                out.push('<div class="rp-md-li">' + it.content + '</div>')
                i++
              }
            } else {
              i++
            }
          }
          return '<div class="rp-md-list">\n' + out.join('\n') + '\n</div>'
        }
        return prefix + render(items, minIndent) + '\n'
      })
      // 16.5 合并"数字列表单项 + 紧跟的 <div class='rp-md-list'> 块"为带嵌套结构
      //   场景：1. **xxx：**\n  - A\n  - B（17 步要求 >= 2 项会跳过单项，导致嵌套 ul 顶到左边）
      //   修复：扫描方式按行处理，遇到数字行后下一行是 <div.rp-md-list> 则合并
      //   关键：平衡 <div> 计数找到 ul 块真正结束位置（不能 [\s\S]*?<\/div> 非贪婪，会在 li 结束就停）
      //   注意：必须在 17 步之前跑（17 步会跳过带 < 的块）
      {
        const lines = html.split('\n')
        const out = []
        let i = 0
        while (i < lines.length) {
          const line = lines[i]
          if (/^\d+\.\s+/.test(line) && i + 1 < lines.length && lines[i + 1].startsWith('<div class="rp-md-list"')) {
            // 找 ul 块的真正结束（平衡 div 计数）
            let depth = 0
            let endLine = i + 1
            for (let j = i + 1; j < lines.length; j++) {
              const l = lines[j]
              const opens = (l.match(/<div\b/g) || []).length
              const closes = (l.match(/<\/div>/g) || []).length
              depth += opens - closes
              if (depth === 0) { endLine = j; break }
            }
            const headerText = line.replace(/^\d+\.\s+/, '').trim()
            const innerList = lines.slice(i + 1, endLine + 1).join('\n')
            out.push('<div class="rp-md-list rp-md-list-num"><div class="rp-md-li">' + headerText + innerList + '</div></div>')
            i = endLine + 1
          } else {
            out.push(line)
            i++
          }
        }
        html = out.join('\n')
      }
      // 17. 数字列表（1. xxx 开头，至少 2 项连续，允许行首缩进）
      //   原 bug：1 项就识别成 <ol>（如 "1. 综合性综述："）造成空列表
      //   修复：+ 改为 {2,} 要求至少 2 项
      //   配合 16.5 步：单项 + 嵌套 ul 已被 16.5 步合并，17 步只处理真正的连续数字列表
      html = html.replace(/(^|\n)((?:[ \t]*\d+\.\s+.+\n?){2,})/g, (m, prefix, list) => {
        const items = list.split('\n').filter(line => line.trim()).map(line => '<div class="rp-md-li">' + line.replace(/^[ \t]*\d+\.\s+/, '') + '</div>').join('')
        return prefix + '<div class="rp-md-list rp-md-list-num">' + items + '</div>'
      })
      // 18. 段落
      html = html.split(/\n{2,}/).map(block => {
        const trimmed = block.trim()
        if (!trimmed) return ''
        if (/^<(h[1-6]|ul|ol|pre|blockquote|hr|p|table|div)/.test(trimmed)) return block
        // 包含 div 列表块（如 16.5 步合并的"数字项+嵌套 ul"）不要被包成 p
        if (/<div class="rp-md-(list|li)"/.test(trimmed)) return block
        return '<p>' + trimmed.replace(/\n/g, '<br/>') + '</p>'
      }).join('\n')
      // 19. 还原 DOI 占位符
      doiHolder.forEach((d, i) => {
        const token = '\x00DOI' + i + '\x00'
        html = html.split(token).join('<a class="rp-doi-link" href="https://doi.org/' + d.doi + '" target="_blank" rel="noopener">' + d.prefix + d.doi + '</a>')
      })
      return html
    }
  }
}
</script>

<style scoped>
/* ====== 完整结果展示页（接真实数据版）====== */
/* Source 对应: .result-panel */
.result-page {
  margin-top: 24px;
  background: #fbfaf6;
  border: 1px solid #dedfd8;
  border-radius: 8px;
  padding: 0;
  overflow: hidden;
  font-family: SimSun, "Songti SC", "STSong", "Noto Serif CJK SC", serif;
  color: #171814;
}

/* Header（顶部耗时条·已合并进报告头） */
.rp-header { display: none; }
.rp-header-left { display: none; }
.rp-header-title { display: none; }
.rp-tool-badge { display: none; }
.rp-header-time { display: none; }

/* ====== 第一行：固定顶栏（运行结果 | 耗时） ====== */
.rp-top-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 18px;
  background: #e8e8e8;
  border-bottom: 1px solid #e0e0d8;
}
.rp-top-bar-title {
  font-size: 15px;
  font-weight: 700;
  color: #171814;
}
.rp-top-bar-time {
  font-size: 13px;
  color: #858b80;
  white-space: nowrap;
}

/* ====== 第二行：报告信息头 ====== */
.rp-report-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 18px;
  margin: 10px 22px;
  background: #fff;
  border-bottom: 1px solid #e8e8e8;
}
.rp-report-info {
  flex: 1;
  min-width: 0;
}
.rp-report-title {
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  color: #171814;
  line-height: 1.3;
}
.rp-report-subtitle {
  margin-top: 4px;
  font-size: 13px;
  color: #5d6259;
  line-height: 1.5;
}
.rp-dot {
  margin: 0 6px;
  color: #858b80;
}
.rp-sub-export {
  color: #5d6259;
}
.rp-report-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
  align-self: center;
}
.rp-action-btn {
  padding: 6px 12px;
  background: #fff;
  border: 1px solid rgba(49, 92, 75, 0.24);
  border-radius: 8px;
  font-size: 12px;
  font-weight: 700;
  color: #203d34;
  cursor: pointer;
  font-family: SimSun, "Songti SC", "STSong", "Noto Serif CJK SC", serif;
  white-space: nowrap;
}
.rp-action-btn:hover {
  border-color: rgba(49, 92, 75, 0.38);
  background: rgba(49, 92, 75, 0.08);
}

/* ====== 4 个统计卡（Source 对应 .nnscholar-metric） ====== */
.rp-stats-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin: 0 22px;
  padding-bottom: 16px;
}
.rp-stat-card {
  display: grid;
  gap: 5px;
  min-height: 104px;
  padding: 14px;
  border: 1px solid rgba(49, 92, 75, 0.16);
  border-radius: 8px;
  background: #fff;
  transition: border-color 0.2s ease;
}
.rp-stat-card:hover {
  border-color: rgba(49, 92, 75, 0.32);
}
.rp-stat-label {
  color: #5d6259;
  font-size: 13px;
  font-weight: 700;
}
.rp-stat-value {
  color: #171814;
  font-family: "Helvetica Neue", Arial, sans-serif;
  font-size: 28px;
  line-height: 1.1;
  font-weight: 700;
  font-feature-settings: 'tnum' 1;
}
.rp-stat-note {
  color: #5d6259;
  font-size: 12px;
  font-style: normal;
  margin-top: auto;
}

/* ====== 底部数据源 chip 行（Source 对应 .nnscholar-filter-row + .nnscholar-filter-chip） ====== */
.rp-source-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin: 0 22px 16px;
}
.rp-source-chip {
  display: inline-flex;
  align-items: center;
  min-height: 26px;
  padding: 3px 9px;
  border: 1px solid rgba(49, 92, 75, 0.18);
  border-radius: 8px;
  background: rgba(49, 92, 75, 0.07);
  color: #203d34;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.2;
  white-space: nowrap;
}
.rp-source-chip:hover {
  border-color: rgba(49, 92, 75, 0.36);
  background: rgba(49, 92, 75, 0.14);
}
/* meta（综合来源/年份） vs source（数据源名）用不同文本颜色区分 */
.rp-source-chip--meta {
  color: #5d6259;
}

/* ====== 真实内容区（markdown 渲染） ====== */
.rp-content {
  background: #fff;
  border: 1px solid #ebe6d9;
  border-radius: 8px;
  padding: 24px 28px;
  margin-bottom: 18px;
  font-size: 14px;
  line-height: 1.85;
  color: #2c3e50;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

/* 标题层次感 */
.rp-content h1, .rp-content h2, .rp-content h3,
.rp-content h4, .rp-content h5, .rp-content h6 {
  color: #1a1a1a;
  font-weight: 700;
  line-height: 1.4;
}
.rp-content h1 {
  font-size: 26px;
  margin: 28px 0 18px;
  border-bottom: 3px solid #c8b894;
  padding-bottom: 8px;
  letter-spacing: -0.3px;
}
.rp-content h1:first-child { margin-top: 0; }
.rp-content h2 {
  font-size: 21px;
  margin: 26px 0 14px;
  padding: 10px 0 8px;
  border-bottom: 1px solid #ebe6d9;
  color: #1a1a1a;
}
.rp-content h2:first-child { margin-top: 0; }
.rp-content h3 {
  font-size: 17px;
  margin: 22px 0 10px;
  padding: 6px 0 6px 12px;
  border-left: 4px solid #c8b894;
  background: linear-gradient(90deg, #faf8f3 0%, transparent 60%);
  line-height: 1.3;
  color: #1a1a1a;
}
.rp-content h4 {
  font-size: 15.5px;
  margin: 18px 0 8px;
  color: #2a2a2a;
  font-weight: 600;
  padding-left: 4px;
  border-left: 2px solid #d8d2c5;
}
.rp-content h5, .rp-content h6 {
  font-size: 14px;
  margin: 14px 0 6px;
  color: #555;
  font-weight: 600;
}

/* 段落 */
.rp-content p {
  margin: 10px 0;
  line-height: 1.85;
  color: #333;
}

/* 列表（div 实现，不依赖 ol/ul/li 元素） */
.rp-content .rp-md-list {
  margin: 8px 0;
  line-height: 1.8;
}
.rp-content .rp-md-li {
  position: relative;
  padding-left: 1.5em;
  margin: 4px 0;
  line-height: 1.8;
}
.rp-content .rp-md-li::before {
  content: "○";
  position: absolute;
  left: 0;
  top: 0;
  color: #8a7e5a;
  font-weight: 600;
}
/* 嵌套列表缩进（通过子 .rp-md-list 累加 padding-left） */
.rp-content .rp-md-list .rp-md-list {
  margin: 4px 0;
  padding-left: 1.2em;
}
/* 列表项内加粗标题（子标题“潜在生物学机制：”等）加粗显示 */
.rp-content .rp-md-li > strong:first-child {
  font-weight: 700;
  color: #1a1a1a;
  margin-right: 0.2em;
}

/* 粗体 / 斜体 */
.rp-content strong {
  color: #1a1a1a;
  font-weight: 700;
}
.rp-content em {
  color: #5d4e2e;
  font-style: italic;
}

/* 引用块 */
.rp-content blockquote {
  margin: 14px 0;
  padding: 10px 16px;
  border-left: 4px solid #8a7e5a;
  background: #faf8f3;
  color: #4a3f2a;
  border-radius: 0 6px 6px 0;
  font-size: 13.5px;
}

/* 行内代码 */
.rp-content code.md-inline-code {
  background: #f5f2ed;
  border: 1px solid #e0dbce;
  padding: 1px 7px;
  border-radius: 3px;
  font-family: 'SFMono-Regular', Consolas, monospace;
  font-size: 13px;
  color: #5d4e2e;
}

/* 代码块 */
.rp-content pre.md-code {
  background: #2a2a2a;
  color: #e8e3d8;
  padding: 14px 16px;
  border-radius: 6px;
  overflow-x: auto;
  font-family: 'SFMono-Regular', Consolas, monospace;
  font-size: 13px;
  line-height: 1.6;
  margin: 14px 0;
}
.rp-content pre.md-code code {
  background: transparent;
  border: none;
  padding: 0;
  color: inherit;
}

/* 链接 */
.rp-content a {
  color: #5d4e2e;
  text-decoration: none;
  border-bottom: 1px solid rgba(93, 78, 46, 0.3);
}
.rp-content a:hover {
  border-bottom-color: #5d4e2e;
}

/* 水平线 */
.rp-content hr {
  border: none;
  border-top: 1px dashed #d8d2c5;
  margin: 20px 0;
}

/* ====== 综合分析卡片（Source 对应 .nnscholar-section + .nnscholar-analysis） ====== */
.rp-analysis-card {
  margin: 0 22px 20px;
  overflow: hidden;
  border: 1px solid #dedfd8;
  border-radius: 8px;
  background: #fff;
}
.rp-analysis-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
  border-bottom: 1px solid rgba(49, 92, 75, 0.14);
  background: rgba(49, 92, 75, 0.045);
}
.rp-analysis-title {
  margin: 0;
  color: #171814;
  font-size: 14px;
  font-weight: 700;
}
.rp-analysis-chip {
  color: #5d6259;
  font-size: 13px;
  flex-shrink: 0;
}
.rp-output-scroll {
  max-height: 500px;
  overflow-y: auto;
  padding: 0;
}

.rp-analysis-body {
  padding: 22px;
  color: #111;
  font-family: SimSun, "Songti SC", "STSong", "Noto Serif CJK SC", serif;
  font-size: 16px;
  line-height: 1.9;
  word-wrap: break-word;
  overflow-wrap: break-word;
}
/* Source 对应 .nnscholar-analysis 内联元素 */
.rp-analysis-content { font-size: 12px; line-height: 1.9; color: #111; }
.rp-analysis-content p { margin: 0 0 14px; line-height: 1.9; color: #111; font-size: 12px; }
.rp-analysis-content h1, .rp-analysis-content h2, .rp-analysis-content h3,
.rp-analysis-content h4 { margin: 16px 0 8px; line-height: 1.5; border: none; padding: 0; font-size: 14px; font-weight: 700; color: #171814; }
.rp-analysis-content h1:first-child,
.rp-analysis-content h2:first-child,
.rp-analysis-content h3:first-child,
.rp-analysis-content h4:first-child,
.rp-analysis-content h1:first-of-type,
.rp-analysis-content h2 + h1,
.rp-analysis-content h2:first-of-type { margin-top: 0; display: none; }
.rp-analysis-content strong { color: #253c34; font-weight: 700; }
.rp-analysis-content em { font-style: italic; color: #111; }
.rp-analysis-content code {
  padding: 1px 5px; border-radius: 5px;
  background: rgba(48, 79, 125, 0.09); color: #304f7d;
  font-family: "Courier New", monospace; font-size: 0.92em; border: none;
}
.rp-analysis-content blockquote {
  margin: 14px 0; padding: 10px 16px; border-left: 4px solid #203d34;
  background: rgba(49, 92, 75, 0.07); color: #5d6259; border-radius: 0; font-size: 14px;
}
.rp-analysis-content a {
  color: #304f7d; font-weight: 400; text-decoration: underline; border: none;
  font-size: inherit;
}
.rp-analysis-content a:hover { text-decoration: underline; }
/* Source 对应 .nnscholar-analysis 内联元素 */
.rp-analysis-body h1, .rp-analysis-body h2, .rp-analysis-body h3,
.rp-analysis-body h4 { margin: 16px 0 8px; line-height: 1.5; font-size: 14px; font-weight: 700; color: #171814; border: none; padding: 0; }
.rp-analysis-body h1:first-child,
.rp-analysis-body h2:first-child,
.rp-analysis-body h3:first-child,
.rp-analysis-body h4:first-child,
.rp-analysis-body h1:first-of-type,
.rp-analysis-body h2 + h1,
.rp-analysis-body h2:first-of-type { margin-top: 0; display: none; }
.rp-analysis-body p { margin: 0 0 14px; line-height: 1.9; color: #111; font-size: 12px; }
.rp-analysis-body strong { color: #253c34; font-weight: 700; }
.rp-analysis-body em { font-style: italic; color: #111; }
.rp-analysis-body blockquote {
  margin: 14px 0; padding: 10px 16px; border-left: 4px solid #203d34;
  background: rgba(49, 92, 75, 0.07); color: #5d6259; border-radius: 0; font-size: 14px;
}
.rp-analysis-body code {
  padding: 1px 5px; border-radius: 5px;
  background: rgba(48, 79, 125, 0.09); color: #304f7d;
  font-family: "Courier New", monospace; font-size: 0.92em; border: none;
}
.rp-analysis-body a {
  color: #304f7d; font-weight: 400; text-decoration: underline; border: none;
  font-size: inherit;
}
.rp-analysis-body a:hover { text-decoration: underline; }
.rp-analysis-body .rp-citation {
  color: #304f7d; font-weight: 400; white-space: nowrap;
  font-size: inherit; padding: 0; background: none; border: none;
  border-radius: 0; margin: 0; letter-spacing: 0;
}
.rp-analysis-body .rp-doi-link {
  color: #304f7d; font-weight: 400; text-decoration: underline;
  font-family: inherit; border: none; font-size: inherit; word-break: break-all;
}
.rp-analysis-body .rp-doi-link:hover { text-decoration: underline; }

/* ====== 检索关键词折叠面板（Source 对应 .nnscholar-query-details） ====== */
.rp-keyword-bar {
  margin: 0 22px 14px;
}
.rp-keyword-details {
  border: 1px solid rgba(49, 92, 75, 0.14);
  border-radius: 8px;
  background: #fff;
  overflow: hidden;
}
.rp-keyword-summary {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 14px;
  font-size: 14px;
  font-weight: 700;
  color: #203d34;
  cursor: pointer;
  list-style: none;
  user-select: none;
  position: relative;
}
.rp-keyword-summary::-webkit-details-marker { display: none; }
.rp-keyword-summary::marker { content: ''; }
.rp-keyword-summary:hover {
  opacity: 0.8;
}
.rp-keyword-icon { display: none; }
.rp-keyword-count {
  margin-left: auto;
  font-size: 12px;
  color: #5d6259;
  font-weight: 400;
}
.rp-keyword-body {
  padding: 0 14px 14px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
/* chip（Source 对应 .nnscholar-filter-chip） */
.rp-keyword-chip {
  display: inline-flex;
  align-items: center;
  min-height: 26px;
  padding: 3px 9px;
  border: 1px solid rgba(49, 92, 75, 0.18);
  border-radius: 8px;
  background: rgba(49, 92, 75, 0.07);
  color: #203d34;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.2;
  cursor: default;
}
.rp-keyword-chip:hover {
  border-color: rgba(49, 92, 75, 0.36);
  background: rgba(49, 92, 75, 0.14);
}
.rp-filter-body {
  display: block;
  padding: 0 14px 14px;
}
.rp-filter-item {
  padding: 6px 0;
  font-size: 13px;
  color: #5d6259;
  line-height: 1.6;
  border-bottom: 1px solid rgba(49, 92, 75, 0.1);
}
.rp-filter-item:last-child {
  border-bottom: none;
}

/* ====== 文献结果表（Source 对应 .nnscholar-section + .nnscholar-table） ====== */
.rp-literature-list {
  margin: 0 22px 20px;
  overflow: hidden;
  border: 1px solid #dedfd8;
  border-radius: 8px;
  background: #fff;
}
.rp-literature-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
  border-bottom: 1px solid rgba(49, 92, 75, 0.14);
  background: rgba(49, 92, 75, 0.045);
}
.rp-lit-head-label {
  margin: 0;
  color: #171814;
  font-size: 14px;
  font-weight: 700;
}
.rp-lit-head-count {
  color: #5d6259;
  font-size: 13px;
}
/* 表格（Source 对应 .nnscholar-table） */
.rp-lit-table {
  width: 100%;
  min-width: 980px;
  border-collapse: collapse;
  table-layout: fixed;
  font-size: 14px;
}
.rp-col-num { width: 54px; }
.rp-col-title { width: 38%; }
.rp-col-journal { width: 22%; }
.rp-col-metrics { width: 18%; }
.rp-col-links { width: 18%; }
/* 表头 */
.rp-lit-table thead th {
  background: #fbfcfb;
  color: #203d34;
  font-size: 14px;
  font-weight: 700;
  text-align: left;
  padding: 12px 14px;
  border-bottom: 1px solid rgba(49, 92, 75, 0.1);
  white-space: nowrap;
}
/* 表格行 */
.rp-lit-table tbody tr:hover {
  background: rgba(49, 92, 75, 0.035);
}
.rp-lit-table td {
  padding: 12px 14px;
  vertical-align: top;
  border-bottom: 1px solid rgba(49, 92, 75, 0.1);
  line-height: 1.5;
  word-wrap: break-word;
  overflow-wrap: break-word;
}
.rp-lit-table tbody tr:last-child td {
  border-bottom: none;
}
/* 序号列 */
.rp-td-num {
  text-align: center;
  color: #5d6259;
  font-family: "Helvetica Neue", Arial, sans-serif;
  font-weight: 700;
}
 
/* 题目列：标题 + 年份/期刊/来源 meta */
.rp-lit-title {
  font-size: 14.5px;
  font-weight: 600;
  color: #171814;
  line-height: 1.45;
  word-wrap: break-word;
  overflow-wrap: break-word;
  margin-bottom: 4px;
}
/* 标题链接（Source 对应 .nnscholar-paper-title a） */
.rp-lit-title-link {
  color: #304f7d;
  font-weight: 400;
  text-decoration: underline;
  word-wrap: break-word;
  overflow-wrap: break-word;
}
.rp-lit-title-link:hover {
  color: #304f7d;
}
.rp-lit-title-link:visited {
  color: #304f7d;
  text-decoration: underline;
}
.rp-lit-meta {
  font-size: 12px;
  color: #5d6259;
  line-height: 1.5;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0 6px;
}
.rp-lit-year { color: #5d6259; font-weight: 700; }
.rp-lit-journal { color: #5d6259; font-weight: 400; }
.rp-lit-source {
  color: #5d6259;
  background: rgba(49, 92, 75, 0.07);
  border: 1px solid rgba(49, 92, 75, 0.14);
  border-radius: 8px;
  padding: 1px 5px;
  font-size: 12px;
  line-height: 1.5;
}
.rp-lit-sep { color: #858b80; }
/* 来源/期刊列 */
.rp-lit-source-tag {
  font-size: 11.5px;
  color: #888;
  margin-top: 3px;
  line-height: 1.4;
}
/* 指标 chip（Source 对应 .nnscholar-quality-badge） */
.rp-lit-metrics {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
}
.rp-lit-chip {
  display: inline-flex;
  align-items: center;
  min-height: 26px;
  padding: 3px 9px;
  border: 1px solid rgba(49, 92, 75, 0.18);
  border-radius: 8px;
  background: rgba(49, 92, 75, 0.07);
  color: #203d34;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.2;
  white-space: nowrap;
}
.rp-lit-chip-if { } /* 都统一用 Source 的绿色系风格 */
.rp-lit-chip-q { }
.rp-lit-chip-cas { }
.rp-lit-chip-cite { }
/* 链接列（Source 对应 .nnscholar-links） */
.rp-lit-links {
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: flex-start;
  min-width: 0;
}
.rp-lit-link {
  color: #304f7d;
  font-size: 13px;
  font-weight: 400;
  text-decoration: underline;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}
.rp-lit-link:hover {
  color: #304f7d;
}
.rp-lit-doi {
  color: #304f7d;
  font-size: 13px;
  font-weight: 400;
  text-decoration: underline;
}
.rp-lit-doi:hover {
  color: #304f7d;
}
.rp-lit-link-text {
  color: #5d6259;
  font-size: 12px;
  word-break: break-all;
  max-width: 100%;
}
@media (max-width: 900px) {
  /* 小屏隐藏指标+链接列，但保留列头可见（在 thead，不隐藏） */
  .rp-col-metrics,
  .rp-col-links {
    display: none;
  }
  .rp-th-metrics,
  .rp-th-links {
    display: none;
  }
  .rp-td-metrics,
  .rp-td-links {
    display: none;
  }
  .rp-col-title { width: 60%; }
  .rp-col-journal { width: 28%; }
}

/* ====== GFM 表格（层次感关键） ====== */
.rp-md-table-wrap {
  margin: 18px 0;
  border-radius: 8px;
  overflow-x: auto;
  border: 1px solid #ebe6d9;
  background: #faf8f3;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}
.rp-md-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  line-height: 1.6;
  table-layout: auto;       /* 关键：让浏览器按内容自适应列宽 */
  min-width: 100%;
}
.rp-md-table thead {
  background: linear-gradient(180deg, #ebe6d9 0%, #e0dbce 100%);
}
.rp-md-table th {
  padding: 12px 14px;
  text-align: left;
  font-weight: 700;
  color: #1a1a1a;
  font-size: 12.5px;
  border-bottom: 2px solid #c8b894;
  white-space: nowrap;
  letter-spacing: 0.3px;
}
.rp-md-table td {
  padding: 12px 14px;
  border-bottom: 1px solid #f0ebdf;
  color: #2c3e50;
  vertical-align: top;
  word-break: break-word;   /* 关键：长单词 / 长 URL 允许换行 */
  overflow-wrap: break-word;
  white-space: normal;
  min-width: 50px;
}
.rp-md-table tbody tr:nth-child(even) td {
  background: #fff;
}
.rp-md-table tbody tr:last-child td {
  border-bottom: none;
}
.rp-md-table tbody tr:hover td {
  background: #f5f0e2;
}
/* 第一列（# 序号）居中且窄 */
.rp-md-table th:first-child,
.rp-md-table td:first-child {
  text-align: center;
  min-width: 40px;
  max-width: 70px;
  font-weight: 600;
  color: #8a7e5a;
}

/* ====== 伪表格的 cell 段 ====== */
p.rp-md-pipe-line {
  margin: 6px 0;
  padding: 10px 14px;
  background: #faf8f3;
  border-left: 3px solid #c8b894;
  border-radius: 0 4px 4px 0;
  color: #2c3e50;
  line-height: 1.7;
}

/* ====== 引用信息标（Source 对应 .citation-mark） ====== */
.rp-citation {
  color: #304f7d;
  font-weight: 400;
  white-space: nowrap;
  font-size: inherit;
  padding: 0;
  background: none;
  border: none;
  border-radius: 0;
  margin: 0;
  letter-spacing: 0;
}

/* ====== DOI 链接（Source 用 <code> 包裹 DOI） ====== */
.rp-doi-link {
  color: #304f7d;
  font-weight: 400;
  text-decoration: underline;
  font-family: inherit;
  font-size: inherit;
  border: none;
  word-break: break-all;
}
.rp-doi-link:hover {
  text-decoration: underline;
}
</style>
